import styles from "./styles/index.module.css";
import navbarStyles from "./styles/navbar.module.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Onboard from "./components/onboard/Onboard";
import NavBar from "./components/navbar/Navbar";
import Branding from "./components/navbar/Branding";
import Credence_Logo from "/assets/credenceidlogo.png";
import Image from "./components/common/Image";
import Landing from "./components/home/Landing";
import BottomBar from "./components/common/BottomBar";
import { ApiErrorProvider } from "./provider/ApiErrorProvider";
import { SessionProvider } from "./provider/SessionProvider";
import {
  ClientSessionProvider,
  useClientSession,
} from "./provider/ClientSessionProvider";
import { ClientProvider } from "./provider/ClientProvider";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.ts"

/**
 * Guards the capture flow.
 *
 * Replaces ProtectedRoute, which asked an identity provider whether the visitor
 * was signed in. There is no sign-in now: the only question is whether an
 * application is in progress, which the client session answers locally without a
 * round trip.
 */
const RequireApplication = ({ children }: { children: JSX.Element }) => {
  const { applicationId } = useClientSession();
  return applicationId ? children : <Navigate to="/" replace />;
};

function App() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t("identity_verification");
  }, [t]);

  return (
    <div className={styles.app}>
      {
        <NavBar>
          <Branding>
            <Image src={Credence_Logo} className={navbarStyles.logo} />
          </Branding>
        </NavBar>
      }
      <Routes>
        {/*
          Open access, fire and forget: the landing page is reachable by anyone
          with the link, and there is no status dashboard to return to. The only
          routes are the landing screen and the capture flow.
        */}
        <Route path="/" element={<Landing />} />
        <Route
          path="/onboard/:id"
          element={
            <RequireApplication>
              <Onboard />
            </RequireApplication>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomBar />
    </div>
  );
}

const AppWrapper = () => {
  return (
    <ApiErrorProvider>
      <SessionProvider>
        <Router>
          {/* Inside Router: the provider navigates on start and on end. */}
          <ClientProvider>
            <ClientSessionProvider>
              <App />
            </ClientSessionProvider>
          </ClientProvider>
        </Router>
      </SessionProvider>
    </ApiErrorProvider>
  );
};

export default AppWrapper;
